import { Injectable } from "@angular/core";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ATDLComparatorService } from "./atdl-comparator.service";

import { ATDLInterfaceControlModel } from "../../data-atdl/model/atdl-interface-control.model";
import { ATDLParameterDataModel } from "../../data-atdl/model/atdl-parameter-data.model";

import { ATDLNLogicOperator } from "../../data-atdl/enums/atdl-logic-operator.enum";
import { ATDLNOperator } from "../../data-atdl/enums/atdl-operator.enum";
import { ATDLNParameterType } from "../../data-atdl/enums/atdl-parameter-type.enum";
import { ATDLDataModel } from "../../data-atdl/model/atdl-data.model";
import { ATDLEnumModel } from "../../data-atdl/model/atdl-enum.model";
import { ATDLValidationCaseModel } from "../../data-atdl/model/atdl-validation-case.model";
import { ATDLValidationGroupModel } from "../../data-atdl/model/atdl-validation-group.model";
import { ATDLValidationRuleModel } from "../../data-atdl/model/atdl-validation-rule.model";
import { ICDAuxCheckboxModel } from "../../data-idc/controls/icd-aux-checkbox.model";
import { ICDBaseModel } from "../../data-idc/controls/icd-base.model";
import { ICDTransportModel } from "../../data-idc/coordination/icd-transport.model";
import { ICDValidationOutcomeModel } from "../../data-idc/coordination/icd-validation-outcome.model";
import { ICNInputControlState } from "../../interface/controls/models/bfx-interface-controls.enums";
import { OELoggerService } from "../basic/oe-logger.service";
import { ATDLUtilityService, ICD_CLOCK_TIME_PATTERN } from "./atdl-utility.service";
import { BfxInputControlStateEnum } from "shared/base-components/models/base-components.enums";

/** Match controls by control name */
const CONTROL_ID_RESOLVER: IdResolver = (item) => item.icdId;
/** Match controls by parameter name */
const PARAMETER_ID_RESOLVER: IdResolver = (item) => item.refName;

/**
 * Type that encompasses ATDLParameterDataModel, ATDLInterfaceControlModel, and
 * ATDLInterfaceAuxiliaryModel - used for locating an identifiable model item in a list
 * that has a value.
 */
type CheckableValueModel = {
  /** Control name */
  icdId: string;
  /** Associates parameter name */
  refName: string;
  /** model value */
  value: any;
};

/** Retuns the ID part of a CheckableValueModel */
type IdResolver = (control: CheckableValueModel) => string;

@Injectable()
export class ATDLValidatorService {
  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    protected oeLogger: OELoggerService,
    private atdlComparator: ATDLComparatorService,
    private atdlUtility: ATDLUtilityService,
  ) {
    this.oeLogger = oeLogger.for(__dirname);
  }

  /**********************************************************************/
  /* CONTROL VALIDATION  */

  public validateModel(model: ATDLDataModel): Set<string> {
    if (model instanceof ATDLDataModel) {
      let cases: Array<ATDLValidationCaseModel> = model.validation;
      let controls: Array<ATDLInterfaceControlModel> = model.controls;

      if (cases instanceof Array && cases.length > 0) {
        let affectedControlIds: Set<string> = this.getValidationAffectedControls(
          model.controls,
          cases,
        );
        // remove duplicates
        let casesValidationOutcome: Array<{
          case: ATDLValidationCaseModel;
          outcome: boolean;
          icdOutcomes: Array<ICDValidationOutcomeModel>;
        }> = new Array<any>();
        for (let caseModel of cases) {
          let icdValidityOutcomes = new Array<ICDValidationOutcomeModel>();
          let caseOutcome: boolean = this.executeICDValidationCase(
            controls,
            caseModel,
            icdValidityOutcomes,
          );
          casesValidationOutcome.push({
            case: caseModel,
            outcome: caseOutcome,
            icdOutcomes: icdValidityOutcomes,
          });
        }

        this.applyControlsValidationResults(casesValidationOutcome, controls, affectedControlIds);

        // Check parameters constraint rules as well
        this.overrideParameterValidation(model, affectedControlIds);

        return affectedControlIds;
      } else {
        return null;
      }
    } else {
      logger.out.error(`ERROR: atdlValidator.validateModel(): Algo definition INVALID `);
      return null;
    }
  }

  /**
   * Check each parameter for validation errors. We need to ensure that any control that is marked as valid
   * after running the validation rules is also checked to ensure parameter is also in valid.
   * If parameter fails validation, then we override the validation state of the control.
   * @param model
   * @param affectControlIds
   */
  private overrideParameterValidation(model: ATDLDataModel, affectControlIds: Set<string>) {
    const errors: ICDTransportModel[] = [];
    // First we get a list of all parameter validation errors
    for (const parameter of model.parameters) {
      this.executeParameterLocalValidation(parameter, errors);
    }
    for (const error of errors) {
      if (affectControlIds.has(error.icdId)) {
        // Let's ensure that the parameter is in an invalid state - if not, due to a rule that set it value, then
        // we override to set invalid again
        const control = model.controls.find((control) => control.icdId === error.icdId);

        if (control && control.validState !== BfxInputControlStateEnum.INVALID) {
          // Not invalid so set invalid and update the error message
          control.validState = BfxInputControlStateEnum.INVALID;
          control.errorMsg = error.errorMsg;
        }
      }
    }
  }

  /**
   * Check the specified ATDLValidationGroupModel against the supplied ATDLModel and return the
   * result of the check- used to determine the applicablity of complete StateRule checks that reference multiple controls
   *
   * @param model
   * @param topLogicGroup
   * @returns
   */
  public validateSingleControlFlowModel(
    model: ATDLDataModel,
    topLogicGroup: ATDLValidationGroupModel,
  ): boolean {
    let retOutcome: boolean = false;

    if (model instanceof ATDLDataModel && topLogicGroup instanceof ATDLValidationGroupModel) {
      let controls: CheckableValueModel[] = model.fullControls;

      let icdValidityOutcomes: ICDValidationOutcomeModel[] = [];
      retOutcome = this.executeValidationLogicGroup(
        CONTROL_ID_RESOLVER,
        controls,
        topLogicGroup,
        icdValidityOutcomes,
        "Unused Error",
      );
    } else {
      logger.out.error(`ERROR: atdlValidator.validateSingleControlFlowModel(): Illegal args`);
    }
    return retOutcome;
  }

  private executeICDValidationCase(
    controls: Array<ATDLInterfaceControlModel>,
    caseModel: ATDLValidationCaseModel,
    icdValidityOutcomes: Array<ICDValidationOutcomeModel>,
  ): boolean {
    if (
      caseModel instanceof ATDLValidationCaseModel &&
      controls instanceof Array &&
      controls.length > 0
    ) {
      // validator rules on root check
      const ruleCount = caseModel.rules?.length ?? 0;
      const groupCount = caseModel.logicGroups?.length ?? 0;

      if (ruleCount + groupCount != 1) {
        logger.out.error(
          `ERROR: atdlValidator.executeParameterValidationCase(): invalid validation rule count found`,
        );
      } else {
        let finalResult;
        if (groupCount == 1) {
          finalResult = this.executeValidationLogicGroup(
            PARAMETER_ID_RESOLVER,
            controls,
            caseModel.logicGroups[0],
            icdValidityOutcomes,
            caseModel.errorMessage,
          );
        } else {
          finalResult = this.executeSingleValidationRule(
            PARAMETER_ID_RESOLVER,
            controls,
            caseModel.rules[0],
            icdValidityOutcomes,
            caseModel.errorMessage,
          );
        }
        this.outputExtendedLogging(
          `ALGO: Validator: FINAL OUTCOME -> Controls [${icdValidityOutcomes
            .map((item) => item.icdId)
            .join(",")}] | FINAL RESULT: ${finalResult ? "VALID" : "INVALID"}`,
        );

        return finalResult;
      }
    } else {
      logger.out.error(`ERROR: atdlValidator.executeICDValidationCase(): Invalid case model`);
      return false;
    }
  }

  private applyControlsValidationResults(
    casesValidationOutcome: Array<any>,
    controls: Array<ATDLInterfaceControlModel>,
    affectedControlIds: Set<string>,
  ): void {
    if (
      casesValidationOutcome instanceof Array &&
      casesValidationOutcome.length > 0 &&
      controls instanceof Array
    ) {
      for (let affectedIcd of affectedControlIds) {
        let targetControl = controls.find((item) => item.icdId === affectedIcd);
        let targetValidationCases = casesValidationOutcome.filter((item) => {
          return item.icdOutcomes.find((subItem) => subItem.icdId === affectedIcd) != null;
        });
        if (
          targetControl instanceof ATDLInterfaceControlModel &&
          targetValidationCases instanceof Array &&
          targetValidationCases.length > 0
        ) {
          let controlInvalidApplied: boolean = false;
          for (let targetValidationCase of targetValidationCases) {
            let caseOutcome: boolean = targetValidationCase.outcome;
            let icdRelevantOutcomes = targetValidationCase.icdOutcomes.filter(
              (item) => item.icdId == affectedIcd,
            );
            if (icdRelevantOutcomes instanceof Array && icdRelevantOutcomes.length > 0) {
              for (let icdValidityOutcome of icdRelevantOutcomes) {
                if (caseOutcome) {
                  // valid
                  if (!controlInvalidApplied) {
                    targetControl.validState = ICNInputControlState.VALID;
                    targetControl.errorMsg = null;
                  }
                } else {
                  // invalid
                  targetControl.validState = ICNInputControlState.INVALID;
                  targetControl.errorMsg = icdValidityOutcome.errorMsg;
                  controlInvalidApplied = true;
                }
              } // validation rule for loop end
            } else {
              logger.out.error(
                `ERROR: atdlValidator.applyControlsValidationResults(): No relevant outcomes for '${affectedIcd}'`,
              );
            }
          } // validation case for loop end
        } else {
          logger.out.error(
            `ERROR: atdlValidator.applyControlsValidationResults(): Control or relevant validation outcomes not found for ${affectedIcd}`,
          );
        }
      } // controls for loop end
    } else {
      logger.out.error(
        `ERROR: atdlValidator.applyControlsValidationResults(): Invalid input parameters`,
      );
    }
  }

  /**********************************************************************/
  /* PARAMETER VALIDATION  */

  public validateParameters(
    model: ATDLDataModel,
    parameters: Array<ATDLParameterDataModel>,
    errors: Array<ICDTransportModel>,
  ): boolean {
    if (model && parameters instanceof Array && parameters.length > 0) {
      let result: boolean = true;
      let cases: Array<ATDLValidationCaseModel> = model.validation;

      // local parameter validation
      for (let parameter of parameters) {
        let localResult = this.executeParameterLocalValidation(parameter, errors);
        result = result && localResult;
      }

      // validation cases
      if (cases instanceof Array && cases.length > 0) {
        for (let caseModel of cases) {
          let caseResult = this.executeParameterValidationCase(parameters, caseModel, errors);
          result = result && caseResult;
        }
      }

      return result;
    }
    return true;
  }

  /************************************/
  /* Parameter Internal Validation  */
  private executeParameterLocalValidation(
    parameter: ATDLParameterDataModel,
    errors: Array<ICDTransportModel>,
  ): boolean {
    if (parameter instanceof ATDLParameterDataModel) {
      let value = this.atdlUtility.getValueFromICDBaseModel(
        parameter.value,
        this.isParamTypeString(parameter),
      );
      if (value != null && value !== "") {
        // belongs to dataset
        let dataItem = null;
        if (parameter.dataset instanceof Array && parameter.dataset.length > 0) {
          if (typeof value === "string") {
            dataItem = parameter.dataset.find((item) => item.enumId === value);
            if (dataItem == null) {
              errors.push(new ICDTransportModel(parameter.icdId, null, "Value not in dataset"));
              return false;
            }
          } else {
            errors.push(
              new ICDTransportModel(parameter.icdId, null, "Dropdown value not a string"),
            );
            return false;
          }
        }

        // Min/Max Length
        if (this.isParamTypeString(parameter)) {
          if (typeof value === "string") {
            if (typeof parameter.minLength === "number" && value.length < parameter.minLength) {
              errors.push(
                new ICDTransportModel(
                  parameter.icdId,
                  null,
                  `Value length is less than minimum allowed length (${parameter.minLength})`,
                ),
              );
              return false;
            }
            if (typeof parameter.maxLength === "number" && value.length > parameter.maxLength) {
              errors.push(
                new ICDTransportModel(
                  parameter.icdId,
                  null,
                  `Value length is greater than maximum allowed length (${parameter.maxLength})`,
                ),
              );
              return false;
            }
          } else {
            errors.push(
              new ICDTransportModel(
                parameter.icdId,
                null,
                `Invalid parameter type must be ${ATDLNParameterType[parameter.paramType]}`,
              ),
            );
            return false;
          }
        } else if (parameter.paramType === ATDLNParameterType.CHAR) {
          if (dataItem instanceof ATDLEnumModel) {
            if (
              typeof dataItem.wireValue !== "string" ||
              (typeof dataItem.wireValue === "string" && dataItem.wireValue.length !== 1)
            ) {
              errors.push(
                new ICDTransportModel(parameter.icdId, null, `Value must be a single character`),
              );
              return false;
            }
          } else if (
            typeof value !== "string" ||
            (typeof value === "string" && value.length !== 1)
          ) {
            errors.push(
              new ICDTransportModel(parameter.icdId, null, `Value must be a single character`),
            );
            return false;
          }
        } else if (
          parameter.paramType === ATDLNParameterType.UTCTIMEONLY ||
          parameter.paramType === ATDLNParameterType.UTCTIMESTAMP
        ) {
          if (typeof value !== "string" || !ICD_CLOCK_TIME_PATTERN.test(value)) {
            errors.push(
              new ICDTransportModel(parameter.icdId, null, `Invalid parameter time format value`),
            );
            return false;
          }

          if (
            typeof parameter.minValue === "string" &&
            ICD_CLOCK_TIME_PATTERN.test(parameter.minValue)
          ) {
            if (!this.atdlUtility.compareTimeGreaterOrEqualThan(value, parameter.minValue)) {
              errors.push(
                new ICDTransportModel(
                  parameter.icdId,
                  null,
                  `Time value is less than minimum allowed '${parameter.minValue}'`,
                ),
              );
              return false;
            }
          }

          if (
            typeof parameter.maxValue === "string" &&
            ICD_CLOCK_TIME_PATTERN.test(parameter.maxValue)
          ) {
            if (!this.atdlUtility.compareTimeGreaterOrEqualThan(parameter.maxValue, value)) {
              errors.push(
                new ICDTransportModel(
                  parameter.icdId,
                  null,
                  `Time value is greater than maximum allowed '${parameter.maxValue}'`,
                ),
              );
              return false;
            }
          }
        } else {
          // Min/Max Value
          // min value
          if (typeof parameter.minValue === "number") {
            if (value instanceof CubiqTS.BigNumber) {
              if (value.isLessThan(parameter.minValue)) {
                errors.push(
                  new ICDTransportModel(
                    parameter.icdId,
                    null,
                    `Value is less than minimum allowed value (${parameter.minValue})`,
                  ),
                );
                return false;
              }
            } else if (typeof value === "number") {
              if (value < parameter.minValue) {
                errors.push(
                  new ICDTransportModel(
                    parameter.icdId,
                    null,
                    `Value is less than minimum allowed value (${parameter.minValue})`,
                  ),
                );
                return false;
              }
            } else if (!isNaN(value) && typeof value === "string") {
              if (Number(value) < parameter.minValue) {
                errors.push(
                  new ICDTransportModel(
                    parameter.icdId,
                    null,
                    `Value is less than minimum allowed value (${parameter.minValue})`,
                  ),
                );
                return false;
              }
            }
          }
          // max value
          if (typeof parameter.maxValue === "number") {
            if (value instanceof CubiqTS.BigNumber) {
              if (value.isGreaterThan(parameter.maxValue)) {
                errors.push(
                  new ICDTransportModel(
                    parameter.icdId,
                    null,
                    `Value is greater than maximum allowed value (${parameter.maxValue})`,
                  ),
                );
                return false;
              }
            } else if (typeof value === "number") {
              if (value > parameter.maxValue) {
                errors.push(
                  new ICDTransportModel(
                    parameter.icdId,
                    null,
                    `Value is greater than maximum allowed value (${parameter.maxValue})`,
                  ),
                );
                return false;
              }
            } else if (!isNaN(value) && typeof value === "string") {
              if (Number(value) > parameter.maxValue) {
                errors.push(
                  new ICDTransportModel(
                    parameter.icdId,
                    null,
                    `Value is greater than maximum allowed value (${parameter.maxValue})`,
                  ),
                );
                return false;
              }
            }
          }
        }

        return true;
      } else if (
        parameter.required &&
        parameter.constValue == null &&
        (value == null || value === "")
      ) {
        errors.push(
          new ICDTransportModel(parameter.icdId, null, "Required parameter has no value"),
        );
        return false;
      } else if (
        parameter.value instanceof ICDBaseModel &&
        parameter.value.auxModel instanceof ICDAuxCheckboxModel &&
        parameter.value.auxModel.enabled &&
        parameter.value.auxModel.value &&
        (value == null || value === "")
      ) {
        errors.push(
          new ICDTransportModel(parameter.icdId, null, "Parameter is enabled, but has no value"),
        );
        return false;
      } else {
        // null but not required
        return true;
      }
    } else {
      logger.out.error(
        `ERROR: atdlValidator.executeParameterLocalValidation(): Invalid parameter model`,
      );
      return true;
    }
  }

  private executeParameterValidationCase(
    parameters: Array<ATDLParameterDataModel>,
    caseModel: ATDLValidationCaseModel,
    errors: Array<ICDTransportModel>,
  ): boolean {
    if (
      caseModel instanceof ATDLValidationCaseModel &&
      parameters instanceof Array &&
      parameters.length > 0
    ) {
      // validator rules on root check
      const ruleCount = caseModel.rules?.length ?? 0;
      const groupCount = caseModel.logicGroups?.length ?? 0;

      if (ruleCount + groupCount != 1) {
        logger.out.error(
          `ERROR: atdlValidator.executeParameterValidationCase(): invalid validation rule count found`,
        );
      } else {
        let icdValidityOutcomes = new Array<ICDValidationOutcomeModel>();
        let finalResult;
        if (groupCount == 1) {
          finalResult = this.executeValidationLogicGroup(
            PARAMETER_ID_RESOLVER,
            parameters,
            caseModel.logicGroups[0],
            icdValidityOutcomes,
            caseModel.errorMessage,
          );
        } else {
          finalResult = this.executeSingleValidationRule(
            PARAMETER_ID_RESOLVER,
            parameters,
            caseModel.rules[0],
            icdValidityOutcomes,
            caseModel.errorMessage,
          );
        }
        this.outputExtendedLogging(
          `ALGO: Validator: FINAL OUTCOME -> PARAMETERS [${icdValidityOutcomes
            .map((item) => item.refName)
            .join(",")}] | FINAL RESULT: ${finalResult ? "VALID" : "INVALID"}`,
        );
        // create errors from validation outcome
        if (!finalResult) {
          // If case fails, then create an error for each parameter member
          for (let icdValidityOutcome of icdValidityOutcomes) {
            let targetParameter = parameters.find(
              (item) => item.icdId === icdValidityOutcome.icdId,
            );
            if (targetParameter instanceof ATDLParameterDataModel) {
              errors.push(
                new ICDTransportModel(icdValidityOutcome.icdId, null, icdValidityOutcome.errorMsg),
              );
            } else {
              logger.out.error(
                `ERROR: atdlValidator.executeParameterValidationCase(): Target control '${icdValidityOutcome.icdId}' NOT FOUND!`,
              );
            }
          }
        }
        return finalResult;
      }
    } else {
      logger.out.error(`ERROR: atdlValidator.executeParameterValidationCase(): Invalid case model`);
      return false;
    }
  }

  /**********************************************************************/
  /* VALIDATION  */

  /**
   * Validate a single logicOperator group
   * @param nameResolver Determines whether the control name or parameter name is used for retrieving value
   * @param valueModels List of data models
   * @param logicGroup  Group to check
   * @param icdValidityOutcomes Output results
   * @param errorMsg Error message to use for invalid outcomes
   * @returns
   */
  private executeValidationLogicGroup(
    nameResolver: IdResolver,
    valueModels: CheckableValueModel[],
    logicGroup: ATDLValidationGroupModel,
    icdValidityOutcomes: Array<ICDValidationOutcomeModel>,
    errorMsg: string,
  ): boolean {
    if (logicGroup instanceof ATDLValidationGroupModel) {
      let outcomes = new Array<boolean>();

      if (logicGroup.rules instanceof Array && logicGroup.rules.length > 0) {
        for (let rule of logicGroup.rules) {
          const result = this.executeSingleValidationRule(
            nameResolver,
            valueModels,
            rule,
            icdValidityOutcomes,
            errorMsg,
          );
          outcomes.push(result);
        }
      }

      if (logicGroup.logicGroups instanceof Array && logicGroup.logicGroups.length > 0) {
        for (let subLogicGroup of logicGroup.logicGroups) {
          outcomes.push(
            this.executeValidationLogicGroup(
              nameResolver,
              valueModels,
              subLogicGroup,
              icdValidityOutcomes,
              errorMsg,
            ),
          );
        }
      }
      return this.applyLogicOperator(outcomes, logicGroup.logicOperator);
    }
  }

  private executeSingleValidationRule(
    nameResolver: IdResolver,
    valueModels: CheckableValueModel[],
    rule: ATDLValidationRuleModel,
    outcomes: Array<ICDValidationOutcomeModel>,
    errorMsg: string,
  ): boolean {
    let fieldOutcome1 = new ICDValidationOutcomeModel("", "", null, errorMsg);
    let fieldOutcome2 = new ICDValidationOutcomeModel("", "", null, errorMsg);
    const result = this.executeValidationRule(
      nameResolver,
      valueModels,
      rule,
      fieldOutcome1,
      fieldOutcome2,
    ); // , errorMsg
    outcomes.push(fieldOutcome1);
    if (fieldOutcome2.refName !== "") {
      outcomes.push(fieldOutcome2);
    }
    return result;
  }

  /**
   * Resolve a single validation into a boolena result
   * @param nameResolver Used to locate correct data model
   * @param valueModels
   * @param rule
   * @param icdValidityOutcomeField
   * @param icdValidityOutcomeField2
   * @returns
   */
  private executeValidationRule(
    nameResolver: IdResolver,
    valueModels: CheckableValueModel[],
    rule: ATDLValidationRuleModel,
    icdValidityOutcomeField: ICDValidationOutcomeModel,
    icdValidityOutcomeField2: ICDValidationOutcomeModel,
  ): boolean {
    try {
      if (rule instanceof ATDLValidationRuleModel) {
        let valueModel1 = valueModels.find((item) => nameResolver(item) === rule.field);
        if (valueModel1?.hasOwnProperty("value")) {
          let value1 = this.atdlUtility.getValueFromICDBaseModel(valueModel1.value);
          let value2 = null;
          let valueModel2 = null;
          // calculate second value if needed
          if (!this.atdlUtility.isSingleParamOperator(rule.operator)) {
            if (typeof rule.field2 === "string") {
              valueModel2 = valueModels.find((item) => nameResolver(item) === rule.field2);
            }

            if (valueModel2?.hasOwnProperty("value")) {
              value2 = this.atdlUtility.getValueFromICDBaseModel(valueModel2.value);
            } else if (typeof rule.value === "string") {
              value2 = rule.value;
            } else {
              logger.out.error(
                `ERROR: atdlValidator.executeValidationRule(): error processing second rule value %O`,
                rule,
              );
            }
          }
          let result = this.executeComparison(value1, value2, rule.operator);
          icdValidityOutcomeField.icdId = valueModel1.icdId;
          icdValidityOutcomeField.refName = valueModel1.refName;
          icdValidityOutcomeField.valid =
            icdValidityOutcomeField.valid === true || icdValidityOutcomeField.valid == null
              ? result
              : icdValidityOutcomeField.valid; // keep same values if previous case/rule failed

          if (
            valueModel2 instanceof ATDLInterfaceControlModel ||
            valueModel2 instanceof ATDLParameterDataModel
          ) {
            icdValidityOutcomeField2.icdId = valueModel2.icdId;
            icdValidityOutcomeField2.refName = valueModel2.refName;
            icdValidityOutcomeField2.valid = icdValidityOutcomeField.valid;
          }
          return result;
        } else {
          logger.out.error(
            `ERROR: atdlValidator.executeValidationRule(): Invalid control! Control refName = '${rule.field}'`,
          );
        }
      } else {
        logger.out.error(`ERROR: atdlValidator.executeValidationRule(): Invalid rule!`);
      }
      return false;
    } catch (ex) {
      logger.out.error(`ERROR: atdlValidator.executeValidationRule(): Exception: ${ex}`);
    }
  }

  private applyLogicOperator(outcomes: Array<boolean>, logicOperator: ATDLNLogicOperator): boolean {
    if (logicOperator != null && logicOperator in ATDLNLogicOperator && outcomes instanceof Array) {
      let result: boolean = null;
      for (let outcome of outcomes) {
        if (result == null) {
          if (logicOperator === ATDLNLogicOperator.NOT && outcomes.length > 1) {
            result = false;
          } else if (logicOperator === ATDLNLogicOperator.NOT && outcomes.length === 1) {
            result = !outcome;
          } else {
            // initial value
            result = outcome;
          }
        } else {
          switch (logicOperator) {
            case ATDLNLogicOperator.AND:
              result = result && outcome;
              break;
            case ATDLNLogicOperator.OR:
              result = result || outcome;
              break;
            case ATDLNLogicOperator.XOR:
              result = (result || outcome) && !(result && outcome);
              break;
            case ATDLNLogicOperator.NOT:
              logger.out.error(
                `ERROR: atdlValidator.applyLogicOperator(): Invalid application of '${ATDLNLogicOperator[logicOperator]}' (operator === NOT) operator on more than one outcome.`,
              );
              result = false;
            default:
              logger.out.error(
                `ERROR: atdlValidator.applyLogicOperator(): Unknown operator '${logicOperator}'`,
              );
              result = false;
          }
        }
      } // for end
      this.outputExtendedLogging(
        `ALGO: Validator: Logic Group -> Outcomes: ${outcomes.toString()} | Operator: ${
          ATDLNLogicOperator[logicOperator]
        } | Result: ${result ? "VALID" : "INVALID"}`,
      );
      return result;
    } else {
      logger.out.error(`ERROR: atdlValidator.applyLogicOperator(): Invalid input parameters`);
      return false;
    }
  }

  private executeComparison(value: any, value2: any, operator: ATDLNOperator): boolean {
    try {
      switch (operator) {
        case ATDLNOperator.EX: // filed value is required
          return value != null;
        case ATDLNOperator.NX: // field value should be null
          return value == null;
        case ATDLNOperator.LE: // filed value is LESS OR EQUAL to secondary value
          return this.atdlComparator.lessThanEquals(value, value2);
        case ATDLNOperator.LT: // filed value is LESS than secondary value
          return this.atdlComparator.lessThan(value, value2);
        case ATDLNOperator.GE: // filed value is GREATER OR EQUAL to secondary value
          return this.atdlComparator.greaterThanEquals(value, value2);
        case ATDLNOperator.GT: // filed value is GREATER then secondary value
          return this.atdlComparator.greaterThan(value, value2);
        case ATDLNOperator.EQ:
          return this.atdlComparator.equals(value, value2);
        case ATDLNOperator.NE:
          return !this.atdlComparator.equals(value, value2);
        default:
          logger.out.error(
            `ERROR: atdlValidator.executeComparison() = Comparison not implemented!`,
          );
          return false;
      }
    } catch (ex) {
      logger.out.error(`ERROR: atdlValidator.executeComparison(): Exception ${ex}`);
    }
  }

  /**
   *
   * @param parameter
   * @returns
   */
  private isParamTypeString(parameter: ATDLParameterDataModel) {
    if (parameter != null && parameter instanceof ATDLParameterDataModel) {
      return (
        parameter.paramType === ATDLNParameterType.STRING ||
        parameter.paramType === ATDLNParameterType.MULTICHAR ||
        parameter.paramType === ATDLNParameterType.MULTISTRING
      );
    }

    return false;
  }

  /**********************************************************************/
  /* DETERMINE VALIDATION CASES FOR ICD  */

  private getValidationCasesForICD(
    model: ATDLDataModel,
    refName: string,
  ): Array<ATDLValidationCaseModel> {
    if (
      model instanceof ATDLDataModel &&
      model.validation instanceof Array &&
      model.validation.length > 0
    ) {
      let result = new Array<ATDLValidationCaseModel>();
      for (let validationCase of model.validation) {
        if (this.traverseValidationCase(validationCase, refName)) {
          result.push(this.atdlUtility.clone<ATDLValidationCaseModel>(validationCase));
        }
      }
      return result.length > 0 ? result : null;
    }
    return null;
  }

  private traverseValidationCase(caseModel: ATDLValidationCaseModel, refName: string): boolean {
    let outcome: boolean = false;
    if (caseModel instanceof ATDLValidationCaseModel) {
      // rules
      if (caseModel.rules instanceof Array && caseModel.rules.length > 0) {
        outcome =
          caseModel.rules.find((item) => item.field === refName || item.field2 === refName) != null;
      }
      // logic groups
      if (!outcome && caseModel.logicGroups instanceof Array && caseModel.logicGroups.length > 0) {
        for (let logicGroup of caseModel.logicGroups) {
          if (!outcome) outcome = this.traverseValidationGroup(logicGroup, refName);
        }
      }
    }
    return outcome;
  }

  private traverseValidationGroup(logicGroup: ATDLValidationGroupModel, refName: string): boolean {
    let outcome: boolean = false;
    if (logicGroup instanceof ATDLValidationGroupModel) {
      // rules
      if (logicGroup.rules instanceof Array && logicGroup.rules.length > 0) {
        outcome =
          logicGroup.rules.find((item) => item.field === refName || item.field2 === refName) !=
          null;
      }
      // logic groups
      if (
        !outcome &&
        logicGroup.logicGroups instanceof Array &&
        logicGroup.logicGroups.length > 0
      ) {
        for (let subLogicGroup of logicGroup.logicGroups) {
          if (!outcome) {
            outcome = this.traverseValidationGroup(subLogicGroup, refName);
          }
        }
      }
    }
    return outcome;
  }

  /**********************************************************************/
  /* FIND AFFECTED CONTROLS BY VALIDATION CASE */

  private getValidationAffectedControls(
    controls: Array<ATDLInterfaceControlModel>,
    caseModels: Array<ATDLValidationCaseModel>,
  ): Set<string> {
    let affectedControlIds: Set<string> = new Set();
    for (let validationCase of caseModels) {
      this.findAffectedControlsInValidationCase(controls, validationCase, affectedControlIds);
    }
    return affectedControlIds;
  }

  private findAffectedControlsInValidationCase(
    controls: Array<ATDLInterfaceControlModel>,
    caseModel: ATDLValidationCaseModel,
    affectedControlIds: Set<string>,
  ): void {
    if (caseModel instanceof ATDLValidationCaseModel) {
      // rules
      if (caseModel.rules instanceof Array && caseModel.rules.length > 0) {
        for (let rule of caseModel.rules) {
          let control1 = controls.find((item) => item.refName === rule.field);
          if (control1 instanceof ATDLInterfaceControlModel) {
            affectedControlIds.add(control1.icdId);
          }
          if (typeof rule.field2 === "string") {
            let control2 = controls.find((item) => item.refName === rule.field2);
            if (control2 instanceof ATDLInterfaceControlModel) {
              affectedControlIds.add(control2.icdId);
            }
          }
        }
      }
      // logic groups
      if (caseModel.logicGroups instanceof Array && caseModel.logicGroups.length > 0) {
        for (let logicGroup of caseModel.logicGroups) {
          this.findAffectedControlsInValidationGroup(controls, logicGroup, affectedControlIds);
        }
      }
    }
  }

  private findAffectedControlsInValidationGroup(
    controls: Array<ATDLInterfaceControlModel>,
    logicGroup: ATDLValidationGroupModel,
    affectedControlIds: Set<string>,
  ): void {
    if (logicGroup instanceof ATDLValidationGroupModel) {
      // rules
      if (logicGroup.rules instanceof Array && logicGroup.rules.length > 0) {
        for (let rule of logicGroup.rules) {
          let control1 = controls.find((item) => item.refName === rule.field);
          if (control1 instanceof ATDLInterfaceControlModel) {
            affectedControlIds.add(control1.icdId);
          }
          if (typeof rule.field2 === "string") {
            let control2 = controls.find((item) => item.refName === rule.field2);
            if (control2 instanceof ATDLInterfaceControlModel) {
              affectedControlIds.add(control2.icdId);
            }
          }
        }
      }
      // logic groups
      if (logicGroup.logicGroups instanceof Array && logicGroup.logicGroups.length > 0) {
        for (let subLogicGroup of logicGroup.logicGroups) {
          this.findAffectedControlsInValidationGroup(controls, subLogicGroup, affectedControlIds);
        }
      }
    }
  }
  /**********************************************************************/
  /* DEBUG - HELPER  */

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputExtendedLogging(value, dump);
  }
}
